import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import SectionBadge from '../components/ui/SectionBadge';
import CtaBanner from '../components/home/CtaBanner';
import { coreServices, getServiceById } from '../lib/servicesData';

const Arrow = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
    <path d="M2 8.5h13M10 3.5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Services = () => {
  const care = getServiceById('maintenance-support');

  return (
    <>
      <Helmet>
        <title>Websites, Custom Software & AI Automation | Nexgeius Toronto</title>
        <meta name="description" content="Nexgeius builds custom websites, business software, and practical AI automation for Toronto businesses. Explore clear starting prices, timelines, and ongoing costs." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.nexgeius.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Websites, Custom Software & AI Automation | Nexgeius" />
        <meta property="og:description" content="Three focused ways to make your business easier to find, easier to run, and easier to scale." />
        <meta property="og:url" content="https://www.nexgeius.com/services" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Nexgeius services',
          itemListElement: coreServices.map((service, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: service.title,
            url: `https://www.nexgeius.com/services/${service.id}`,
          })),
        })}</script>
      </Helmet>

      <main>
        <section className="page-grid relative overflow-hidden bg-[#F6F8FA] px-6 pb-20 pt-32 lg:px-8 lg:pb-24">
          <div className="relative z-10 mx-auto max-w-7xl">
            <SectionBadge>Three ways we can help</SectionBadge>
            <div className="mt-6 grid items-end gap-8 lg:grid-cols-[1.15fr_.85fr]">
              <h1 className="max-w-4xl text-[clamp(2.75rem,6vw,5.8rem)] font-semibold leading-[0.96] tracking-[-0.055em] text-[#102A3A]">
                Built around the work your business needs to do.
              </h1>
              <div className="max-w-xl lg:justify-self-end">
                <p className="text-lg leading-8 text-[#52636E]">
                  A sharper website, a system that replaces busywork, or an AI workflow with sensible guardrails. We scope the smallest useful version first and make the costs visible.
                </p>
                <Link to="/contact" className="button-primary mt-7 inline-flex items-center gap-2">
                  Tell us what is slowing you down <Arrow />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white px-6 py-20 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 flex flex-col justify-between gap-4 border-b border-[#DCE4E8] pb-6 md:flex-row md:items-end">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#176B8A]">Core services</p>
                <h2 className="mt-3 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A] md:text-4xl">Choose the outcome, not the technology.</h2>
              </div>
              <p className="max-w-md text-sm leading-6 text-[#667781]">Every starting price is before tax and third-party subscriptions. You receive a written scope before a build begins.</p>
            </div>

            <div className="space-y-5">
              {coreServices.map((service, index) => (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="service-row group grid gap-8 rounded-[1.75rem] border border-[#DCE4E8] bg-[#FBFCFD] p-7 transition duration-300 hover:-translate-y-1 hover:border-[#AFC4CE] hover:shadow-[0_22px_60px_rgba(16,42,58,0.1)] md:p-9 lg:grid-cols-[.23fr_.9fr_1.1fr_.35fr] lg:items-start"
                >
                  <div className="flex items-center justify-between lg:block">
                    <span className="text-sm font-semibold tabular-nums text-[#8A9AA3]">0{index + 1}</span>
                    <div className="service-icon mt-0 flex h-11 w-11 items-center justify-center rounded-xl text-white lg:mt-7" style={{ backgroundColor: service.accent }}>
                      {service.icon}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: service.accent }}>{service.eyebrow}</p>
                    <h3 className="mt-3 text-2xl font-semibold tracking-[-0.03em] text-[#102A3A] md:text-3xl">{service.title}</h3>
                    <p className="mt-4 text-sm leading-6 text-[#63747E]">Best for {service.bestFor.toLowerCase()}.</p>
                  </div>
                  <div>
                    <p className="max-w-2xl text-base leading-7 text-[#52636E]">{service.shortDesc}</p>
                    <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#DCE4E8] pt-5">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8A9AA3]">Starting at</p>
                        <p className="mt-1 font-semibold text-[#102A3A]">{service.startingAt}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8A9AA3]">Typical timeline</p>
                        <p className="mt-1 font-semibold text-[#102A3A]">{service.timeline}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#176B8A] lg:justify-end">
                    Explore service <span className="transition-transform duration-300 group-hover:translate-x-1"><Arrow /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#102A3A] px-6 py-16 text-white lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[.8fr_1.2fr_.35fr] lg:items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#92C5D8]">After launch</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-[-0.035em]">{care.title}</h2>
            </div>
            <div>
              <p className="max-w-2xl text-base leading-7 text-[#C5D3DA]">{care.shortDesc}</p>
              <p className="mt-3 text-sm font-semibold text-white">Plans start at {care.startingAt}.</p>
            </div>
            <Link to={`/services/${care.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-white lg:justify-end">
              See care plans <Arrow />
            </Link>
          </div>
        </section>

        <section className="bg-[#F6F8FA] px-6 py-20 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-[.75fr_1.25fr]">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#176B8A]">How we choose</p>
                <h2 className="mt-4 max-w-md text-3xl font-semibold tracking-[-0.035em] text-[#102A3A] md:text-4xl">Start with the constraint that matters most.</h2>
              </div>
              <div className="grid gap-px overflow-hidden rounded-2xl border border-[#DCE4E8] bg-[#DCE4E8] sm:grid-cols-3">
                {[
                  ['People cannot find or trust us', 'Start with a website that makes the offer and next step unmistakable.'],
                  ['Our work is scattered and manual', 'Map the workflow, then build the smallest software system that can replace it.'],
                  ['A repetitive task consumes the team', 'Pilot one bounded automation and measure quality, time, and running cost.'],
                ].map(([title, copy]) => (
                  <div key={title} className="bg-white p-6">
                    <h3 className="font-semibold leading-6 text-[#102A3A]">{title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[#63747E]">{copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <CtaBanner />
      </main>
    </>
  );
};

export default Services;
